import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BellRing, CalendarClock, CheckSquare, Plus } from "lucide-react";
import clsx from "clsx";
import { Contracts, type ContractGroup, type ContractUpcoming } from "@/api/contracts";
import TopBar from "@/components/TopBar";
import { Empty, Segmented } from "@/components/ui";
import { ContractRow } from "@/components/contracts/ContractBits";
import NewContractDialog from "@/components/contracts/NewContractDialog";
import { daysUntil, inDays } from "@/lib/contracts";
import { eur, fmtDay } from "@/lib/format";

const UPCOMING_ICON = { end: CalendarClock, notice: BellRing, milestone: CheckSquare } as const;

/** Every contract from first draft to signature, and what's coming up once signed. */
export default function ContractsPage() {
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<ContractGroup>("open");
  const [creating, setCreating] = useState(false);

  // One fetch of everything powers the numbers; the list filters it.
  const allQuery = useQuery({
    queryKey: ["contracts", { q }],
    queryFn: () => Contracts.list({ q: q || undefined }),
  });
  const upcomingQuery = useQuery({
    queryKey: ["contracts", "upcoming"],
    queryFn: Contracts.upcoming,
  });

  const all = allQuery.data ?? [];
  const open = all.filter(
    (c) => !["signed", "expired", "terminated", "cancelled"].includes(c.status),
  );
  const rows =
    group === "all"
      ? all
      : group === "open"
        ? open
        : group === "signed"
          ? all.filter((c) => c.status === "signed")
          : all.filter((c) => ["expired", "terminated", "cancelled"].includes(c.status));
  const signedValue = all
    .filter((c) => c.status === "signed")
    .reduce((n, c) => n + (c.value_eur ?? 0), 0);

  const stats = [
    { label: "In progress", value: String(open.length) },
    {
      label: "Your move",
      value: String(open.filter((c) => c.waiting_on === "us").length),
      warn: open.some((c) => c.waiting_on === "us"),
    },
    {
      label: "Out for signature",
      value: String(open.filter((c) => c.status === "out_for_signature").length),
    },
    { label: "Signed value", value: eur(signedValue, { short: true }) },
  ];

  return (
    <>
      <TopBar
        title="Contracts"
        subtitle="From first draft to signature, and the dates that matter after."
        onSearch={setQ}
        searchPlaceholder="Search contracts or accounts…"
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5" /> New contract
          </button>
        }
      />

      <div className="space-y-5 px-4 py-4 md:px-6 md:py-6">
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="panel px-4 py-3">
              <dt className="text-2xs font-medium text-mut-500">{s.label}</dt>
              <dd
                className={clsx(
                  "mt-0.5 text-xl font-semibold tabular-nums",
                  s.warn && "text-accent-amber",
                )}
              >
                {s.value}
              </dd>
            </div>
          ))}
        </dl>

        {(upcomingQuery.data ?? []).length > 0 && <ComingUp items={upcomingQuery.data ?? []} />}

        <Segmented<ContractGroup>
          label="Which contracts"
          value={group}
          onChange={setGroup}
          options={[
            { value: "open", label: "In progress" },
            { value: "signed", label: "Signed" },
            { value: "ended", label: "Ended" },
            { value: "all", label: "All" },
          ]}
        />

        <div className="panel">
          {allQuery.isLoading ? (
            <p className="p-6 text-md text-mut-500">Loading…</p>
          ) : rows.length === 0 ? (
            <Empty title={q ? "No contracts match" : "No contracts here yet"}>
              {group === "open" && !q
                ? "Start one when a deal gets to paperwork: drafts, redlines and signatures all live in its thread."
                : null}
            </Empty>
          ) : (
            <ul className="divide-y divide-fg/[0.05]">
              {rows.map((c) => (
                <ContractRow key={c.id} contract={c} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {creating && <NewContractDialog onClose={() => setCreating(false)} />}
    </>
  );
}

/** Notice deadlines, end dates and obligations, soonest first. */
function ComingUp({ items }: { items: ContractUpcoming[] }) {
  return (
    <section className="panel px-4 py-3">
      <h2 className="section-title mb-2">Coming up</h2>
      <ul className="space-y-1">
        {items.slice(0, 6).map((u) => {
          const Icon = UPCOMING_ICON[u.kind];
          const n = daysUntil(u.on);
          const urgent = n <= 7;
          return (
            <li key={`${u.kind}-${u.contract_id}-${u.milestone_id ?? u.on}`}>
              <Link
                to={`/contracts/${u.contract_id}`}
                className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-fg/[0.04]"
              >
                <Icon
                  className={clsx(
                    "h-4 w-4 shrink-0",
                    urgent ? "text-accent-amber" : "text-mut-500",
                  )}
                />
                <span className="min-w-0 flex-1 truncate text-md">
                  <span className="font-medium">{u.label}</span>
                  <span className="text-mut-500">
                    {" "}
                    · {u.contract_title}
                    {u.account_name ? ` · ${u.account_name}` : ""}
                  </span>
                </span>
                <span
                  className={clsx(
                    "shrink-0 text-sm tabular-nums",
                    urgent ? "font-medium text-accent-amber" : "text-mut-500",
                  )}
                  title={fmtDay(u.on)}
                >
                  {inDays(n)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
